import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AllRequests from "./tables/AllRequests";
import LeaveRequests from "./tables/LeaveRequests";
import CorrectionRequests from "./tables/CorrectionRequests";
import RaiseRequest from "./RaiseRequest";
import { RiseLoader } from "react-spinners";
import { Search } from "lucide-react";
import SortDropDown from "../../Components/SortDropDown";
import {
  getAllLeaveApprovals,
  getAllLeaveApprovalsApi,
  getAllServices,
  getAttendance,
  getClaimsTypes,
  getCorrectionApprovals,
  getCorrectionApprovalsAPi,
  getExpensiveTypes,
  getLeaveApprovals,
  getLeaveApprovalsApi,
} from "../../ApiHelper/url_Helpers";
import { sortApprovals, sortApprovalsStatus } from "../Dashboard/DashboardData";
import FilterDropdown from "./ApprovalFilter";

/* ---------------- Debounce Hook ---------------- */
const useDebounce = (value, delay = 1000) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debouncedValue;
};

const states = [
  { id: 1, title: "All Requests", path: "/approvals/allrequests" },
  { id: 2, title: "Leave Requests", path: "/approvals/leavesrequests" },
  {
    id: 3,
    title: "Correction Requests",
    path: "/approvals/correctionrequests",
  },
];

export const Approvals = () => {
  const fetchedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [isOpen, setIsOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 1000);

  const [approvalFilters, setApprovalFilters] = useState({
    status: "",
    category: "",
    ordering: "",
  });

  const [allLeaves, setAllLeaves] = useState();
  const [Leaves, setLeaves] = useState();
  const [CorrectionLeaves, setCorrectionLeaves] = useState();
  const [servicesTypes, setServicesTypes] = useState();
  const [leavetypes, setLeavesTypes] = useState();
  const Options = leavetypes?.map((item) => ({
    label: item?.name,
    key: item?.name,
  }));
  const  categoryLeaves= activeTab === 1 ? servicesTypes : Options;

  const [allLeavesPage, setAllLeavesPage] = useState(1);
  const [leavesPage, setLeavesPage] = useState(1);
  const [correctionPage, setCorrectionPage] = useState(1);

  const [expensiveType, setExpensiveType] = useState();
  const [claimType, setClaimsType] = useState();
  const [reimburseType] = useState();

  /* ---------------- Helpers ---------------- */
  const orderingMap = {
    request_newest: "-request_id",
    request_oldest: "request_id",
    status_asc: "status",
    status_desc: "-status",
  };

  const buildQueryString = () => {
    const params = new URLSearchParams();

    if (debouncedSearch) params.set("request_id", debouncedSearch);
    if (approvalFilters.status) params.set("status", approvalFilters.status);
    if (approvalFilters.category)
      params.set("category", approvalFilters.category);
    if (approvalFilters.ordering)
      params.set("ordering", approvalFilters.ordering);

    return params.toString() ? `?${params.toString()}` : "";
  };

  const buildRouteWithParams = (tab, page) => {
    const params = new URLSearchParams();

    if (debouncedSearch) params.set("request_id", debouncedSearch);
    if (approvalFilters.status) params.set("status", approvalFilters.status);
    if (approvalFilters.category)
      params.set("category", approvalFilters.category);
    if (approvalFilters.ordering)
      params.set("ordering", approvalFilters.ordering);
    if (page) params.set("page", page);

    const base =
      tab === 1
        ? "/approvals/allrequests"
        : tab === 2
          ? "/approvals/leavesrequests"
          : "/approvals/correctionrequests";

    return `${base}?${params.toString()}`;
  };

  /* ---------------- API Calls ---------------- */

  const getInitialData = async () => {
    try {
      setLoading(true);
      const leaveresult = await getAttendance();
      const seviceresult = await getAllServices();
      const [all, leave, correction] = await Promise.all([
        getAllLeaveApprovals(),
        getLeaveApprovals(),
        getCorrectionApprovals(),
      ]);

      const claims = await getClaimsTypes();
      const exp = await getExpensiveTypes();
      setServicesTypes(seviceresult?.results);
      setLeavesTypes(leaveresult?.results?.leave_types);
      setAllLeaves(all);
      setLeaves(leave);
      setCorrectionLeaves(correction);
      setClaimsType(claims);
      setExpensiveType(exp);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRequestsWithFilters = async () => {
    setLoading(true);
    const res = await getAllLeaveApprovals(buildQueryString());
    setAllLeaves(res);
    setLoading(false);
  };

  const fetchLeaveRequestsWithFilters = async () => {
    setLoading(true);
    const res = await getLeaveApprovals(buildQueryString());
    setLeaves(res);
    setLoading(false);
  };

  const fetchCorrectionRequestsWithFilters = async () => {
    setLoading(true);
    const res = await getCorrectionApprovals(buildQueryString());
    setCorrectionLeaves(res);
    setLoading(false);
  };

  const handlePagination = (url, type) => {
    if (!url) return;
    const page = new URL(url, window.location.origin).searchParams.get("page");
    navigate(buildRouteWithParams(activeTab, page));

    if (type === "all") getAllLeaveApprovalsApi(url).then(setAllLeaves);
    if (type === "leave") getLeaveApprovalsApi(url).then(setLeaves);
    if (type === "correction")
      getCorrectionApprovalsAPi(url).then(setCorrectionLeaves);
  };

  /* ---------------- URL Sync ---------------- */

  useEffect(() => {
    if (location.pathname.includes("leaves")) setActiveTab(2);
    else if (location.pathname.includes("correction")) setActiveTab(3);
    else setActiveTab(1);

    const params = new URLSearchParams(location.search);

    setSearchQuery(params.get("request_id") || "");
    setApprovalFilters({
      status: params.get("status") || "",
      category: params.get("category") || "",
      ordering: params.get("ordering") || "",
    });
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) return;

    navigate(buildRouteWithParams(activeTab, 1), { replace: true });

    if (activeTab === 1) fetchAllRequestsWithFilters();
    if (activeTab === 2) fetchLeaveRequestsWithFilters();
    if (activeTab === 3) fetchCorrectionRequestsWithFilters();
  }, [activeTab, debouncedSearch, approvalFilters]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getInitialData();
  }, []);

  const list = { expensiveType, claimType, reimburseType };

  /* ---------------- Render ---------------- */

  const renderTable = () => {
    if (activeTab === 1)
      return (
        <AllRequests
          data={allLeaves || []}
          nextPageUrl={allLeaves?.next}
          prevPageUrl={allLeaves?.previous}
          onPageChange={(u) => handlePagination(u, "all")}
          totalCount={allLeaves?.count}
          page={allLeavesPage}
        />
      );

    if (activeTab === 2)
      return (
        <LeaveRequests
          data={Leaves || []}
          nextPageUrl={Leaves?.next}
          prevPageUrl={Leaves?.previous}
          onPageChange={(u) => handlePagination(u, "leave")}
          totalCount={Leaves?.count}
          page={leavesPage}
          onRefresh={fetchLeaveRequestsWithFilters}
        />
      );

    if (activeTab === 3)
      return (
        <CorrectionRequests
          data={CorrectionLeaves || []}
          nextPageUrl={CorrectionLeaves?.next}
          prevPageUrl={CorrectionLeaves?.previous}
          onPageChange={(u) => handlePagination(u, "correction")}
          totalCount={CorrectionLeaves?.count}
          page={correctionPage}
        />
      );
  };

  return (
    <div className="py-4 p-4 md:p-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Approvals</h2>
          <p className="text-sm text-gray-500">
            Review and manage requests from your team.
          </p>
        </div>
        <button
          onClick={() => setIsOpen(true)}
          className="px-4 py-2 bg-cyan-500 text-white rounded-lg"
        >
          Raise Request
        </button>
      </div>

      <div className="flex gap-4 mt-4">
        {states.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-2 font-semibold ${
              activeTab === tab.id
                ? "border-b-2 border-cyan-500 text-gray-900"
                : "text-gray-500"
            }`}
          >
            {tab.title}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 mt-4">
        <div className="relative flex-grow sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search... (e.g. LRQ-100)"
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
          />
        </div>

        <div className="flex gap-2 md:gap-4 w-full sm:w-auto overflow-visible">
          {activeTab !== 3 && (
            <div className="relative">
              <FilterDropdown
                list={categoryLeaves}
                title="Category"
                keyName="category"
                name="Category"
              value={approvalFilters}
              onChange={(next) =>
                setApprovalFilters((prev) => ({ ...prev, ...next }))
              }
              onClear={() => setApprovalFilters({ category: "" })}
            />
          </div>)}
          <div className="relative">
            <FilterDropdown
              list={sortApprovalsStatus}
              title="Status"
              keyName="status"
              name="Status"
              value={approvalFilters}
              onChange={(next) =>
                setApprovalFilters((prev) => ({ ...prev, ...next }))
              }
              onClear={() => setApprovalFilters({ status: "" })}
            />
          </div>

          <div className="relative">
            <SortDropDown
              value={approvalFilters.ordering}
              onChange={(value) =>
                setApprovalFilters((s) => ({ ...s, ordering: value }))
              }
              options={sortApprovals}
            />
          </div>

          <button
            type="button"
            onClick={() => {
              setSearchQuery("");
              setApprovalFilters({ status: "", category: "", ordering: "" });
              setAllLeavesPage(1);
              setLeavesPage(1);
              setCorrectionPage(1);
              navigate(location.pathname, { replace: true });

              if (activeTab === 1) getAllLeaveApprovals().then(setAllLeaves);
              if (activeTab === 2) getLeaveApprovals().then(setLeaves);
              if (activeTab === 3)
                getCorrectionApprovals().then(setCorrectionLeaves);
            }}
            className="flex-1 sm:flex-none  border-gray-300 border rounded-md py-2 px-4 text-sm font-semibold text-gray-500 hover:bg-gray-100"
          >
            Reset All
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[300px]">
          <RiseLoader color="#35e6e9" />
        </div>
      ) : (
        <div className="mt-4">{renderTable()}</div>
      )}

      {isOpen && (
        <RaiseRequest
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          list={list}
          oncLickAction={getInitialData}
        />
      )}
    </div>
  );
};

export default Approvals;
